<template>
    <div class="ds-ex-copy-picker" v-click-outside="cancel" @keydown.esc.stop="cancel">
        <button type="button" class="ds-ex-copy-add" :disabled="disabled" :aria-expanded="isOpen" aria-haspopup="dialog" @click="open">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            Даты
        </button>
        <div v-if="isOpen" class="ds-glass-menu ds-ex-copy-popover" role="dialog" aria-label="Даты копирования">
            <AppCalendar
                :model-value="draft"
                mode="multi"
                :source-dates="mainDay ? [mainDay] : []"
                :min-date="minDay"
                :initial-date="mainDay ?? minDay"
                :day-labels="dayLabels"
                :outside-days-selectable="false"
                large
                copy-state-styles
                @update:model-value="onSelect"
            />
            <div class="ds-ex-copy-summary">
                <div class="ds-ex-copy-summary-count">
                    <span>Добавится</span>
                    <b>{{ countLabel(draft.length, VACANCY_FORMS) }}</b>
                </div>
                <div class="ds-ex-copy-summary-text">
                    <span>{{ draft.length ? datesText : 'Выберите даты в календаре' }}</span>
                    <span v-if="caption" class="ds-ex-muted">{{ caption }}</span>
                </div>
            </div>
            <div class="ds-ex-copy-actions">
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="cancel">Отмена</button>
                <button type="button" class="ds-glass-btn ds-glass-btn-primary" @click="apply">Готово</button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import AppCalendar from '@/ui/AppCalendar.vue'
import type { CalendarDayLabel } from '@/ui/calendar'
import { useCalendarToday } from '@/ui/calendarTime'
import { countLabel, dayMonthLabel, VACANCY_FORMS } from './exchangeFormat'

const props = defineProps<{
    mainDate: string | null
    // «Грузчик · 09:00–18:00» под списком дат.
    caption: string
    timeLabel: string
    disabled?: boolean
}>()

// Даты копий YYYY-MM-DD; основная дата в них не входит.
const dates = defineModel<string[]>({ required: true })

const today = useCalendarToday()
const isOpen = ref(false)
const draft = ref<Dayjs[]>([])

const mainDay = computed(() => props.mainDate ? dayjs(props.mainDate) : null)
const minDay = computed(() => today.value)

const dayLabels = computed<Record<string, CalendarDayLabel>>(() => {
    const labels: Record<string, CalendarDayLabel> = {}
    if (props.mainDate) labels[props.mainDate] = { text: 'основная' }
    for (const day of draft.value) labels[day.format('YYYY-MM-DD')] = { text: `+${props.timeLabel}` }
    return labels
})

const datesText = computed(() => {
    const sorted = [...draft.value].sort((a, b) => a.valueOf() - b.valueOf())
    return sorted.map(day => dayMonthLabel(day.format('YYYY-MM-DD'))).join(', ')
})

function open() {
    draft.value = dates.value.map(day => dayjs(day))
    isOpen.value = true
}

function onSelect(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    const list = Array.isArray(value) ? value as Dayjs[] : [value as Dayjs]
    draft.value = list.filter(day => day && day.format('YYYY-MM-DD') !== props.mainDate)
}

function cancel() {
    if (!isOpen.value) return
    isOpen.value = false
}

function apply() {
    const unique = [...new Set(draft.value.map(day => day.format('YYYY-MM-DD')))].filter(day => day !== props.mainDate).sort()
    dates.value = unique
    isOpen.value = false
}
</script>
